/**
 * Utility for calculating map viewport pan clamp bounds when surround is enabled.
 * Limits how far users can pan from the venue center to reduce tile data egress.
 */

export const DEFAULT_MAX_PAN_MILES = 5; // Default max pan distance from center in miles
const MILES_TO_DEGREES_LAT = 1 / 69.0; // ~1 degree latitude ≈ 69 miles
type PanClampBounds = [sw: [lng: number, lat: number], ne: [lng: number, lat: number]];

/**
 * Converts miles to degrees longitude at a given latitude.
 * Longitude degrees shrink toward the poles by cos(lat).
 * @param {number} miles
 * @param {number} lat - latitude in degrees
 * @returns {number} degrees longitude
 */
const milesToDegreesLng = (miles: number, lat: number): number => {
  const latRad = (lat * Math.PI) / 180;
  return miles / (69.0 * Math.cos(latRad));
};

/**
 * Calculates a MapLibre-compatible maxBounds from a center point and distance in miles.
 * @param {number[]} venueCenter - [lat, lng]
 * @param {number} maxPanMiles - maximum pan distance from center in miles
 * @returns {[[number, number], [number, number]]} [[sw_lng, sw_lat], [ne_lng, ne_lat]]
 */
export const calculatePanClampBounds = (
  venueCenter: [lat: number, lng: number],
  maxPanMiles: number,
): PanClampBounds => {
  const [lat, lng] = venueCenter;
  const dLat = maxPanMiles * MILES_TO_DEGREES_LAT;
  const dLng = milesToDegreesLng(maxPanMiles, lat);
  return [
    [lng - dLng, lat - dLat], // SW
    [lng + dLng, lat + dLat], // NE
  ];
};
